import { getItem } from './items';
import type { EquipmentSlot, EquippedMap, ItemDef, MeshAsset, Rarity } from './types';

/**
 * Per-rarity tint applied to equipment meshes — gives visual variety even when
 * multiple items share the same base mesh file (e.g. all common helmets reuse
 * Crown.obj). Subtle on common, vibrant on legendary.
 */
const RARITY_TINT: Record<Rarity, string> = {
  common:    '#c8d0d8',  // light steel
  uncommon:  '#9af0b8',  // green
  rare:      '#6ab4ff',  // blue
  epic:      '#c87aff',  // purple
  legendary: '#ffd84a',  // gold
  mythic:    '#ff4af0',  // hot pink — Tier-3 ascended
};

/**
 * Per-rarity self-emissive intensity. Common items stay matte, higher tiers
 * pick up a subtle inner glow that reads under bloom. Items can still override
 * this via mesh.emissive / mesh.emissiveIntensity (e.g. legendary set pieces
 * with stronger custom glow).
 */
const RARITY_EMISSIVE_INTENSITY: Record<Rarity, number> = {
  common:    0,
  uncommon:  0.06,
  rare:      0.12,
  epic:      0.20,
  legendary: 0.35,
  mythic:    0.55,
};

/** Set-specific accent overrides (set items always look the same regardless of individual rarity). */
const SET_TINT: Record<string, string> = {
  hollow_knight: '#2a2640',  // void-purple (matches the Hollow-Knight cloak palette)
  vault_knight: '#1a3a6b',   // storm-blue — Vault-Knight runs a cobalt + electric-cyan palette
};

function tintFor(def: ItemDef): string {
  if (def.setId && SET_TINT[def.setId]) return SET_TINT[def.setId];
  return RARITY_TINT[def.rarity] ?? '#ffffff';
}

/** Canonical default body — Hollow Knight chibi body with Bip01_* underscore
 *  rig, exported from authoring/hollow_knight_character_v1.blend. Cyber-Knight
 *  was retired 2026-05-11 in favor of HK as the sole playable class. */
export const DEFAULT_BODY_PATH = '/assets/3d/sets/hollow_knight/base.glb';

/**
 * Returns the path to the character's body glTF based on equipped armor.
 * Each armor item with a body-slot mesh points to its own color-variant of the
 * Vanguard-Knight (rendered by tools/sprite_pipeline as separate .glb files).
 * Equipping different armor visually swaps the entire knight's color scheme.
 */
export function getCharacterBodyPath(equipment: EquippedMap): string {
  const armorInst = equipment.armor;
  if (!armorInst) return DEFAULT_BODY_PATH;
  const def = getItem(armorInst.itemId);
  if (def?.mesh && def.mesh.slot === 'body') return def.mesh.path;
  return DEFAULT_BODY_PATH;
}

/**
 * Body-colour tint derived from the equipped armor. Without this the body
 * GLTF stays its baked palette while helmet/shield attachments pick up the
 * rarity / set tint — so a full Vault-Knight set rendered with the helm
 * and shield blue but the chestpiece white. Returning a tint here lets
 * `<PlayerCharacter bodyColorTint=...>` LERP washed-out body submeshes
 * toward the set/rarity colour, keeping cobalt armour parts that are
 * already cobalt untouched (the LERP is gated on per-mesh lightness).
 *
 * Returns null for armorless characters and for armor that's common /
 * uncommon (those would visually clash with a forced palette shift).
 */
export function getBodyTint(equipment: EquippedMap): { color: string; strength: number } | null {
  const armorInst = equipment.armor;
  if (!armorInst) return null;
  const def = getItem(armorInst.itemId);
  if (!def) return null;
  // Strength values mirror MeshAttachment.tsx TINT_STRENGTH_BY_HEX. Set tints
  // use 1.0 (hard replace) so helm + shield + body all land on EXACT set
  // colour regardless of their respective baseline materials — full set is
  // supposed to read as one cohesive armour, not three blues with different
  // saturation.
  if (def.setId && SET_TINT[def.setId]) {
    return { color: SET_TINT[def.setId], strength: 1.0 };
  }
  // Rarity-driven tint only kicks in at epic+ — common/uncommon/rare stays neutral.
  if (def.rarity === 'common' || def.rarity === 'uncommon' || def.rarity === 'rare') return null;
  // epic 0.65 / legendary 0.80 / mythic 0.92 — same gradient as attachments.
  const strength = def.rarity === 'mythic' ? 0.92 : def.rarity === 'legendary' ? 0.80 : 0.65;
  return { color: RARITY_TINT[def.rarity], strength };
}

export type AttackKind = 'melee' | 'ranged';

/**
 * Determines whether the equipped weapon is ranged (bow / gun-style) or melee.
 * Used to pick the right attack animation and to spawn arrow projectiles on hit.
 */
export function getAttackKind(equipment: EquippedMap): AttackKind {
  const wep = equipment.weapon;
  if (!wep) return 'melee';
  const def = getItem(wep.itemId);
  // Energy / projectile weapons (lightning, fire) are ranged regardless of mesh.
  // Without this, weapons that lack a mesh entry — e.g. legendary `zeus_bolt`
  // with attackVisual:'lightning' — fall through to melee detection and the
  // auto-tick walks the player into striking distance instead of firing.
  if (def?.attackVisual === 'lightning' || def?.attackVisual === 'fire') return 'ranged';
  const path = def?.mesh?.path ?? '';
  if (/Bow|Gun|Crossbow|Pistol|Staff/i.test(path)) return 'ranged';
  return 'melee';
}

/** Sub-classification of a ranged attack — picks the projectile visual.
 *  - 'frost_sphere' for staff weapons (Diablo-2-Frost-Sphere style icy orb)
 *  - 'arrow' for bow/gun/crossbow */
export function getProjectileKind(equipment: EquippedMap): 'arrow' | 'frost_sphere' {
  const wep = equipment.weapon;
  if (!wep) return 'arrow';
  const path = getItem(wep.itemId)?.mesh?.path ?? '';
  if (/Staff/i.test(path)) return 'frost_sphere';
  return 'arrow';
}

/**
 * Returns all attachment-style meshes (weapons on hand, helmets on head) for the equipment.
 * Body-slot meshes (armor → full-character swap) are excluded — those go through
 * `getCharacterBodyPath` instead.
 */
/** Slots that get a procedurally-rendered attachment when the item def has
 *  no mesh of its own. Matches the renderer-side detection in MeshAttachment. */
const PROCEDURAL_SLOTS: readonly EquipmentSlot[] = ['shoulder', 'belt', 'sash'];

export function getMeshLayers(equipment: EquippedMap): MeshAsset[] {
  const layers: MeshAsset[] = [];
  // 'armor' is included so skinned-armor pieces (vault chest etc.) attach
  // to the base body. Items with mesh.slot === 'body' fall through to the
  // body-swap path via getCharacterBodyPath instead.
  for (const slot of ['weapon', 'helmet', 'accessory', 'shield', 'shoulder', 'belt', 'sash', 'armor'] as const) {
    const inst = equipment[slot];
    if (!inst) continue;
    const def = getItem(inst.itemId);
    if (!def) continue;

    // No explicit mesh, but the slot has a procedural fallback → synthesise a
    // MeshAsset that drives the procedural renderer. Lets us ship belt / sash /
    // shoulder items without authoring a GLTF per item.
    if (!def.mesh && PROCEDURAL_SLOTS.includes(slot)) {
      const tintColor = tintFor(def);
      const rarityEmissive = RARITY_EMISSIVE_INTENSITY[def.rarity] ?? 0;
      layers.push({
        path: `procedural://${slot}`,
        slot: slot as MeshAsset['slot'],
        tintColor,
        itemId: def.id,
        ...(rarityEmissive > 0 ? { emissive: tintColor, emissiveIntensity: rarityEmissive } : {}),
      });
      continue;
    }

    if (!def.mesh) continue;
    if (def.mesh.slot === 'body') continue;

    const tintColor = def.mesh.tintColor ?? tintFor(def);
    const rarityEmissive = RARITY_EMISSIVE_INTENSITY[def.rarity] ?? 0;

    // Layer-emissive resolution:
    //   - asset already specifies emissive → keep its values (legendary
    //     custom glow stays correct, e.g. zeus_bolt yellow)
    //   - else if rarity ≥ uncommon → seed a soft emissive in the rarity
    //     colour so even common-mesh items glow a notch when they're rare+
    //   - else → leave matte
    let emissive = def.mesh.emissive;
    let emissiveIntensity = def.mesh.emissiveIntensity;
    if (!emissive && rarityEmissive > 0) {
      emissive = tintColor;
      emissiveIntensity = rarityEmissive;
    }

    layers.push({
      ...def.mesh,
      tintColor,
      itemId: def.id,
      ...(emissive ? { emissive } : {}),
      ...(emissiveIntensity !== undefined ? { emissiveIntensity } : {}),
    });
  }
  return layers;
}
